/**
 * AuthGate — session-based gating for the dashboard.
 *
 * Shieldz uses wallet sign-in (SIWE) as the primary auth. The dashboard
 * routes load behind this gate; if there's no live session, we redirect
 * to /login. The Telegram Mini App path (where init-data is present) is
 * also accepted — auth defers to the backend's authAdmin(), which tries
 * Bearer session first, then Telegram-Init-Data.
 *
 * Public-marketing pages live at https://shieldz.cash. This subdomain
 * is the app surface — there is no marketing landing here.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { getStoredSession, fetchMe } from "../lib/session";

const PUBLIC_ROUTES = new Set(["/login", "/signup", "/claim"]);

const hasInitData = (): boolean => {
  const w = window as unknown as {
    Telegram?: { WebApp?: { initData?: string } };
  };
  const raw = w.Telegram?.WebApp?.initData;
  return typeof raw === "string" && raw.length > 0;
};

export function AuthGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Public routes (login/signup) never need an auth check
      if (PUBLIC_ROUTES.has(location.pathname)) {
        setAuthed(true);
        return;
      }
      // TMA path: Telegram-Init-Data envelope present, trust it for boot
      if (hasInitData() && !getStoredSession()) {
        setAuthed(true);
        return;
      }
      // Web path: require a live session
      const token = getStoredSession();
      if (!token) {
        if (!cancelled) {
          setAuthed(false);
          void navigate({
            to: "/login",
            search: { redirect: location.pathname },
          });
        }
        return;
      }
      try {
        const me = await fetchMe();
        if (cancelled) return;
        if (me) {
          setAuthed(true);
        } else {
          setAuthed(false);
          void navigate({
            to: "/login",
            search: { redirect: location.pathname },
          });
        }
      } catch {
        if (cancelled) return;
        setAuthed(false);
        void navigate({
          to: "/login",
          search: { redirect: location.pathname },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  if (authed === null) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "var(--bg, #fafbfc)",
          color: "var(--ink-3, #94a3b8)",
          fontFamily: "Geist, system-ui, sans-serif",
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  if (!authed) {
    // Render-while-redirecting placeholder
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--bg, #fafbfc)",
        }}
      />
    );
  }

  return <>{children}</>;
}
